import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import Navbar from '../components/common/Navbar';
import Footer from '../components/common/Footer';
import { Eye, Clock, Shield, CheckCircle, AlertTriangle, Calendar, Phone, Star, Users, Award, Heart, Sparkles, ArrowRight } from 'lucide-react';

const Blefaroplastica = () => {
  const [isVisible, setIsVisible] = useState(false);

  useEffect(() => {
    setIsVisible(true);
  }, []);

  const benefits = [
    { icon: Eye, title: 'Aspetto Ringiovanito', description: 'Palpebre più aperte e sguardo più fresco e luminoso' },
    { icon: Sparkles, title: 'Risultati Naturali', description: 'Tecnica mini-invasiva per un risultato armonico' },
    { icon: Clock, title: 'Recupero Rapido', description: 'Ritorno alle attività normali in 7-10 giorni' },
    { icon: Shield, title: 'Procedura Sicura', description: 'Intervento ambulatoriale in anestesia locale' },
  ];

  const procedures = [
    {
      name: 'Blefaroplastica Superiore',
      description: 'Rimozione dell\'eccesso di pelle e grasso dalle palpebre superiori',
      details: [
        'Elimina le palpebre cadenti (dermatocalasi)',
        'Amplia il campo visivo se la pelle copre l\'occhio',
        'Incisione nascosta nella piega palpebrale',
        'Risultati visibili già dopo 2 settimane'
      ],
      duration: '45-60 minuti',
      recovery: '7-10 giorni'
    },
    {
      name: 'Blefaroplastica Inferiore',
      description: 'Correzione delle borse sotto gli occhi e delle rughe',
      details: [
        'Rimuove le borse adipose sotto gli occhi',
        'Riduce le occhiaie e le rughe periorbitali',
        'Approccio transcongiuntivale (senza cicatrici visibili)',
        'Risultato più riposato e giovane'
      ],
      duration: '60-90 minuti',
      recovery: '10-14 giorni'
    },
    {
      name: 'Blefaroplastica Completa',
      description: 'Intervento combinato su palpebre superiori e inferiori',
      details: [
        'Ringiovanimento completo dello sguardo',
        'Risultato più armonico e naturale',
        'Un solo intervento, un solo recupero',
        'Ideale per ringiovanimento globale'
      ],
      duration: '90-120 minuti',
      recovery: '10-14 giorni'
    }
  ];

  const candidateChecklist = [
    'Palpebre superiori cadenti che coprono parzialmente la vista',
    'Eccesso di pelle che crea pieghe sulle palpebre',
    'Borse adipose sotto gli occhi',
    'Aspetto stanco e invecchiato nonostante il riposo',
    'Buone condizioni di salute generale',
    'Aspettative realistiche sui risultati'
  ];

  const faq = [
    {
      question: 'La blefaroplastica è dolorosa?',
      answer: 'L\'intervento viene eseguito in anestesia locale con sedazione, quindi non si avverte dolore durante la procedura. Nei giorni successivi può esserci un leggero fastidio, controllabile con normali antidolorifici.'
    },
    {
      question: 'Quanto durano i risultati?',
      answer: 'I risultati della blefaroplastica sono molto duraturi, tipicamente 10-15 anni per le palpebre superiori. I tessuti continueranno a invecchiare naturalmente, ma non torneranno mai alla condizione pre-operatoria.'
    },
    {
      question: 'Ci saranno cicatrici visibili?',
      answer: 'Le incisioni vengono nascoste nella piega naturale della palpebra superiore e/o all\'interno della palpebra inferiore. Le cicatrici sono praticamente invisibili una volta guarite.'
    },
    {
      question: 'Quando posso tornare al lavoro?',
      answer: 'La maggior parte dei pazienti torna alle attività normali dopo 7-10 giorni. È consigliabile evitare attività fisica intensa per 3-4 settimane.'
    },
    {
      question: 'La blefaroplastica è mutuabile?',
      answer: 'Se la palpebra cadente limita il campo visivo (ptosi funzionale documentata), l\'intervento può essere coperto dal SSN. Nei casi puramente estetici è a carico del paziente.'
    }
  ];

  return (
    <div className="min-h-screen flex flex-col" style={{ background: 'linear-gradient(160deg, #eaf5fc 0%, #f7fbfe 50%, #e1eef8 100%)' }}>
      <Navbar />
      
      {/* Hero Section */}
      <section className="relative pt-32 pb-20 overflow-hidden">
        <div 
          className="absolute inset-0 opacity-10"
          style={{
            backgroundImage: `url("data:image/svg+xml,%3Csvg width='60' height='60' viewBox='0 0 60 60' xmlns='http://www.w3.org/2000/svg'%3E%3Cg fill='none' fill-rule='evenodd'%3E%3Cg fill='%232A9FCB' fill-opacity='0.4'%3E%3Cpath d='M36 34v-4h-2v4h-4v2h4v4h2v-4h4v-2h-4zm0-30V0h-2v4h-4v2h4v4h2V6h4V4h-4zM6 34v-4H4v4H0v2h4v4h2v-4h4v-2H6zM6 4V0H4v4H0v2h4v4h2V6h4V4H6z'/%3E%3C/g%3E%3C/g%3E%3C/svg%3E")`,
          }}
        />
        
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div className={`transition-all duration-1000 ${isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'}`}>
              <span 
                className="inline-block px-4 py-2 rounded-full text-sm font-medium mb-6"
                style={{ background: 'rgba(42, 159, 203, 0.1)', color: '#2A9FCB' }}
              >
                Chirurgia Estetica Oculare
              </span>
              <h1 
                className="text-4xl sm:text-5xl lg:text-6xl mb-6 leading-tight"
                style={{ fontFamily: "'Playfair Display', Georgia, serif", color: '#1A1A1A' }}
              >
                Blefaroplastica
              </h1>
              <p 
                className="text-lg sm:text-xl mb-8 leading-relaxed"
                style={{ fontFamily: "'Manrope', sans-serif", color: '#666666' }}
              >
                Ringiovanisci il tuo sguardo con la chirurgia delle palpebre. 
                Un intervento sicuro e mini-invasivo per eliminare palpebre cadenti, 
                borse sotto gli occhi e donare luminosità al tuo viso.
              </p>
              <div className="flex flex-col sm:flex-row gap-4">
                <Link 
                  to="/prenota" 
                  className="inline-flex items-center justify-center bg-[#2A9FCB] text-white hover:bg-[#1C729E] rounded-full px-8 py-4 text-sm font-medium transition-all duration-300 shadow-lg hover:shadow-xl"
                  style={{ fontFamily: "'Manrope', sans-serif" }}
                >
                  <Calendar className="w-5 h-5 mr-2" />
                  Prenota Consulenza
                </Link>
                <a 
                  href="tel:+390123456789" 
                  className="inline-flex items-center justify-center border-2 border-[#2A9FCB] text-[#2A9FCB] hover:bg-[#E6F4FA] rounded-full px-8 py-4 text-sm font-medium transition-all duration-300"
                  style={{ fontFamily: "'Manrope', sans-serif" }}
                >
                  <Phone className="w-5 h-5 mr-2" />
                  Chiama Ora
                </a>
              </div>
            </div>
            
            <div className={`relative transition-all duration-1000 delay-300 ${isVisible ? 'opacity-100 translate-x-0' : 'opacity-0 translate-x-10'}`}>
              <img 
                src="https://images.pexels.com/photos/5069432/pexels-photo-5069432.jpeg?auto=compress&cs=tinysrgb&w=800"
                alt="Blefaroplastica - Chirurgia delle palpebre"
                className="rounded-3xl shadow-2xl w-full object-cover"
                style={{ maxHeight: '500px' }}
              />
              <div 
                className="absolute -bottom-6 -left-6 bg-white rounded-2xl p-6 shadow-xl"
                style={{ maxWidth: '280px' }}
              >
                <div className="flex items-center gap-3 mb-3">
                  <div className="w-12 h-12 rounded-full bg-[#E6F4FA] flex items-center justify-center">
                    <Award className="w-6 h-6 text-[#2A9FCB]" />
                  </div>
                  <div>
                    <p className="text-2xl font-bold text-[#2A9FCB]">15+</p>
                    <p className="text-sm text-gray-500">Anni di Esperienza</p>
                  </div>
                </div>
                <p className="text-sm text-gray-600">Oltre 2.000 interventi eseguiti con successo</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Benefits Section */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 
              className="text-3xl sm:text-4xl mb-4"
              style={{ fontFamily: "'Playfair Display', Georgia, serif", color: '#1A1A1A' }}
            >
              Perché Scegliere la Blefaroplastica
            </h2>
            <p className="text-lg max-w-2xl mx-auto" style={{ color: '#666666' }}>
              Un intervento che combina benefici estetici e funzionali
            </p>
          </div>
          
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
            {benefits.map((benefit, idx) => (
              <div 
                key={idx}
                className="p-8 rounded-2xl bg-gradient-to-br from-[#e8f4fd] via-[#f4f9fe] to-[#dcedf8] hover:shadow-xl transition-all duration-300 hover:-translate-y-2 border border-transparent hover:border-[#2A9FCB]/20"
              >
                <div className="w-14 h-14 rounded-full bg-[#E6F4FA] flex items-center justify-center mb-6">
                  <benefit.icon className="w-7 h-7 text-[#2A9FCB]" />
                </div>
                <h3 className="text-xl font-semibold mb-3" style={{ color: '#1A1A1A' }}>{benefit.title}</h3>
                <p className="text-gray-600">{benefit.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Procedures Section */}
      <section className="py-20" style={{ background: 'linear-gradient(160deg, #eaf5fc 0%, #f7fbfe 50%, #e1eef8 100%)' }}>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 
              className="text-3xl sm:text-4xl mb-4"
              style={{ fontFamily: "'Playfair Display', Georgia, serif", color: '#1A1A1A' }}
            >
              Tipologie di Intervento
            </h2>
            <p className="text-lg max-w-2xl mx-auto" style={{ color: '#666666' }}>
              Soluzioni personalizzate in base alle tue esigenze
            </p>
          </div>
          
          <div className="grid lg:grid-cols-3 gap-8">
            {procedures.map((proc, idx) => (
              <div 
                key={idx}
                className="bg-white rounded-3xl p-8 shadow-lg hover:shadow-2xl transition-all duration-300 border border-gray-100"
              >
                <div className="flex items-center gap-3 mb-6">
                  <div className="w-10 h-10 rounded-full bg-[#2A9FCB] flex items-center justify-center text-white font-bold">
                    {idx + 1}
                  </div>
                  <h3 className="text-xl font-semibold" style={{ color: '#1A1A1A' }}>{proc.name}</h3>
                </div>
                <p className="text-gray-600 mb-6">{proc.description}</p>
                <ul className="space-y-3 mb-6">
                  {proc.details.map((detail, i) => (
                    <li key={i} className="flex items-start gap-3">
                      <CheckCircle className="w-5 h-5 text-[#2A9FCB] flex-shrink-0 mt-0.5" />
                      <span className="text-gray-700 text-sm">{detail}</span>
                    </li>
                  ))}
                </ul>
                <div className="pt-6 border-t border-gray-100 flex justify-between text-sm">
                  <div>
                    <p className="text-gray-500">Durata</p>
                    <p className="font-semibold text-[#2A9FCB]">{proc.duration}</p>
                  </div>
                  <div className="text-right">
                    <p className="text-gray-500">Recupero</p>
                    <p className="font-semibold text-[#2A9FCB]">{proc.recovery}</p>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Candidate Section */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-16 items-center">
            <div>
              <h2 
                className="text-3xl sm:text-4xl mb-6"
                style={{ fontFamily: "'Playfair Display', Georgia, serif", color: '#1A1A1A' }}
              >
                Sei un Candidato Ideale?
              </h2>
              <p className="text-lg text-gray-600 mb-8">
                La blefaroplastica è indicata per chi presenta uno o più di questi segni:
              </p>
              <ul className="space-y-4">
                {candidateChecklist.map((item, idx) => (
                  <li key={idx} className="flex items-center gap-4">
                    <div className="w-8 h-8 rounded-full bg-[#E6F4FA] flex items-center justify-center flex-shrink-0">
                      <CheckCircle className="w-5 h-5 text-[#2A9FCB]" />
                    </div>
                    <span className="text-gray-700">{item}</span>
                  </li>
                ))}
              </ul>
            </div>
            <div className="relative">
              <img 
                src="https://images.pexels.com/photos/5069608/pexels-photo-5069608.jpeg?auto=compress&cs=tinysrgb&w=800"
                alt="Consulenza blefaroplastica"
                className="rounded-3xl shadow-xl"
              />
              <div className="absolute -bottom-8 -right-8 bg-[#2A9FCB] rounded-2xl p-6 text-white max-w-xs">
                <p className="text-sm opacity-90 mb-2">Consulenza Personalizzata</p>
                <p className="text-lg font-semibold">Valutiamo insieme la soluzione migliore per te</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* FAQ Section */}
      <section className="py-20" style={{ background: 'linear-gradient(160deg, #eaf5fc 0%, #f7fbfe 50%, #e1eef8 100%)' }}>
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 
              className="text-3xl sm:text-4xl mb-4"
              style={{ fontFamily: "'Playfair Display', Georgia, serif", color: '#1A1A1A' }}
            >
              Domande Frequenti
            </h2>
          </div>
          
          <div className="space-y-4">
            {faq.map((item, idx) => (
              <details 
                key={idx}
                className="bg-white rounded-2xl shadow-sm border border-gray-100 group"
              >
                <summary className="p-6 cursor-pointer flex items-center justify-between font-semibold text-[#1A1A1A] hover:text-[#2A9FCB] transition-colors">
                  {item.question}
                  <ArrowRight className="w-5 h-5 transform group-open:rotate-90 transition-transform text-[#2A9FCB]" />
                </summary>
                <div className="px-6 pb-6 text-gray-600">
                  {item.answer}
                </div>
              </details>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 bg-[#2A9FCB]">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 
            className="text-3xl sm:text-4xl text-white mb-6"
            style={{ fontFamily: "'Playfair Display', Georgia, serif" }}
          >
            Prenota la Tua Consulenza
          </h2>
          <p className="text-white/80 text-lg mb-8 max-w-2xl mx-auto">
            Il primo passo verso uno sguardo più giovane e luminoso. 
            Prenota una visita per una valutazione personalizzata.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link 
              to="/prenota" 
              className="inline-flex items-center justify-center bg-white text-[#2A9FCB] hover:bg-gray-100 rounded-full px-10 py-4 text-sm font-semibold transition-all duration-300 shadow-lg"
            >
              <Calendar className="w-5 h-5 mr-2" />
              Prenota Online
            </Link>
            <a 
              href="tel:+390123456789" 
              className="inline-flex items-center justify-center border-2 border-white text-white hover:bg-white/10 rounded-full px-10 py-4 text-sm font-semibold transition-all duration-300"
            >
              <Phone className="w-5 h-5 mr-2" />
              Chiama: 012 345 6789
            </a>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default Blefaroplastica;
